import { Helmet } from '@dr.pogodin/react-helmet';
import { useApiRead } from 'api-read-hook';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-typed-form';
import { Api, useApiCallable } from 'src/api';
import { ApiError, getApiErrorMessage } from 'src/api/hub-api-request';
import {
  Button,
  EmptyState,
  PageError,
  PageLoading,
  PageWrap,
  Stack,
} from 'src/components';
import { Color } from 'src/constants';

import BackToProjects from '../components/BackToProjects';
import EditStageModal from '../components/EditStageModal';
import SaveBar from '../components/SaveBar';
import ServiceOrderDetailsCard from '../components/ServiceOrderDetailsCard';
import ServiceOrderPageHeader from '../components/ServiceOrderPageHeader';
import ServiceOrderStagesCard from '../components/ServiceOrderStagesCard';
import {
  type ServiceOrderFormValues,
  type StageFormValues,
  buildServiceOrderWriteBody,
  emptyStage,
} from '../service-order-form';

type ConstraintViolation = {
  propertyPath: string;
  message: string;
};

type ConstraintViolationListResponse = {
  '@type': 'ConstraintViolationList';
  violations: ConstraintViolation[];
};

function isConstraintViolationList(
  data: Record<string, unknown> | undefined
): data is ConstraintViolationListResponse {
  return (
    data?.['@type'] === 'ConstraintViolationList' &&
    Array.isArray(data['violations'])
  );
}

export default function NewServiceOrderPage() {
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('company');
  const projectId = searchParams.get('project');

  if (!companyId) {
    return (
      <PageWrap background={Color.neutral5} limitWidth="small">
        <Stack gap={1}>
          <BackToProjects />
          <EmptyState
            title="No client chosen"
            subtitle="Choose a client from the Projects screen to add a service order to."
          />
        </Stack>
      </PageWrap>
    );
  }

  return <NewServiceOrderForm companyId={companyId} projectId={projectId} />;
}

function NewServiceOrderForm({
  companyId,
  projectId,
}: {
  companyId: string;
  projectId: string | null;
}) {
  const navigate = useNavigate();
  const callApi = useApiCallable();

  const apiCompany = Api.Company.useReadItem(companyId);
  const apiProject = useApiRead<Api.Project.V>(
    projectId ? `/projects/${projectId}` : null
  );

  const form = useForm<ServiceOrderFormValues>({
    defaultValues: {
      name: '',
      referenceNumber: null,
      budget: '',
      dayLimit: null,
      stages: [emptyStage()],
    },
    onSubmit: async (values, formObject) => {
      if (!apiCompany.data) return false;
      formObject.setLoading(true);
      try {
        const body = buildServiceOrderWriteBody(
          values,
          apiCompany.data['@id'],
          projectId ? `/projects/${projectId}` : null
        );
        const response = await callApi<Api.ServiceOrder.V_List>(
          '/service-orders',
          { method: 'POST', jsonBody: body }
        );
        if (response) navigate(`/projects/service-orders/${response.data.id}`);
      } catch (err) {
        const data =
          err instanceof ApiError
            ? (err.response?.data as Record<string, unknown> | undefined)
            : undefined;
        if (isConstraintViolationList(data)) {
          data.violations.forEach((violation) =>
            formObject.addSubmitError(
              violation.propertyPath as keyof ServiceOrderFormValues,
              violation.message
            )
          );
        } else {
          alert(getApiErrorMessage(err));
        }
      } finally {
        formObject.setLoading(false);
      }
      return true;
    },
  });

  const [editingStage, setEditingStage] = React.useState<number | null>(null);

  const commitStage = (stage: StageFormValues) => {
    if (editingStage === null) return;
    const stages = [...form.values.stages];
    stages[editingStage] = stage;
    form.getField('stages').handleValueChange(stages);
    setEditingStage(null);
  };

  const removeStage = () => {
    if (editingStage === null) return;
    const stages = [...form.values.stages];
    stages.splice(editingStage, 1);
    form.getField('stages').handleValueChange(stages);
    setEditingStage(null);
  };

  const addStage = () => {
    form
      .getField('stages')
      .handleValueChange([...form.values.stages, emptyStage()]);
  };

  if (apiCompany.error) return <PageError {...apiCompany} />;
  const company = apiCompany.data;
  if (!company) return <PageLoading />;

  const projectName = apiProject.data?.name ?? null;
  const meta = `${company.name} · new, nothing written yet`;
  const canCreate = (form.values.name?.trim() ?? '') !== '' && !form.isLoading;

  return (
    <PageWrap background={Color.neutral5} limitWidth="small">
      <Helmet>
        <title>New service order</title>
      </Helmet>

      <Stack gap={1}>
        <BackToProjects />
        <ServiceOrderPageHeader title="New service order" meta={meta} />
        <ServiceOrderDetailsCard
          form={form}
          companyName={company.name}
          projectName={projectName}
        />
        <ServiceOrderStagesCard
          stages={form.values.stages}
          onAddStage={addStage}
          onEditStage={setEditingStage}
          footnote="Stages can be left for later."
        />
      </Stack>

      <SaveBar>
        <Button
          label="Cancel"
          variant="text"
          colorScheme="black"
          onClick={() => navigate('/projects')}
        />
        <Button
          label="Create service order"
          onClick={form.handleSubmit}
          disabled={!canCreate}
        />
      </SaveBar>

      {editingStage !== null && (
        <EditStageModal
          stage={form.values.stages[editingStage]}
          onSave={commitStage}
          onRemove={removeStage}
          onClose={() => setEditingStage(null)}
        />
      )}
    </PageWrap>
  );
}
